"use client";

import { useState } from "react";
import { Dialog, DialogContent } from "./dialog";
import { Button } from "./button";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { useUatFeedbackMutation } from "../hooks/useFeedback";

interface CallFeedbackFormProps {
    isOpen: boolean;
    voiceConfig: any;
    call_id: string;
    isCallDialogOpen: boolean;
    setIsCallDialogOpen: (isOpen: boolean) => void;
    setIsFeedbackFormOpen: (isOpen: boolean) => void;
    isOutboundCall: boolean;
}

export function CallFeedbackForm({ isOpen, voiceConfig, call_id, setIsFeedbackFormOpen, setIsCallDialogOpen, isOutboundCall }: CallFeedbackFormProps) {
    const [callQuality, setCallQuality] = useState("");
    const [suggestions, setSuggestions] = useState("");
    const [showDialog, setShowDialog] = useState(false);
    const [severity, setSeverity] = useState("");
    const [userName, setUserName] = useState("");
    const { mutate: sendFeedback } = useUatFeedbackMutation();

    const handleSubmit = (feedbackCategory: string) => {
        const payload: any = {
            baseUrl: voiceConfig.baseUrl,
            agent_id: voiceConfig.agent_id,
            tenant_id: voiceConfig.tenant_id,
            workspace_id: voiceConfig.workspace_id,
            call_id: call_id,
            feedback_category: feedbackCategory,
            user_name: userName
        };
        if (severity) payload.issue_severity = severity;
        if (callQuality.trim()) payload.issue_description = callQuality.trim();
        if (suggestions.trim()) payload.expected_behavior = suggestions.trim();
        sendFeedback(payload);
        setIsCallDialogOpen(false);
        setIsFeedbackFormOpen(false);
    };

    return (
        <Dialog open={isOpen}>
            <DialogContent className="max-w-[400px] grid content-center bg-white">
                <main className="h-full grid content-center">
                    {/* Our agent will call you—please provide your feedback after the call */}
                    <h2 className="text-xl text-center mb-6">{isOutboundCall
                        ? "Please provide your feedback after the call"
                        : "How was your experience?"}</h2>
                    <div className="flex justify-center gap-8 p-4">
                        <button
                            onClick={() => {
                                handleSubmit("positive");
                                setIsCallDialogOpen(false);
                                setIsFeedbackFormOpen(false);
                            }}
                            className="flex flex-col items-center transition-transform"
                        >
                            <ThumbsUp
                                size={30}
                                strokeWidth={1}
                                className="text-green-500 transition-all hover:fill-green-100"
                            />
                            <span className="mt-2">Good</span>
                        </button>

                        <button
                            onClick={() => {
                                setShowDialog(true);
                            }}
                            className="flex flex-col items-center transition-transform"
                        >
                            <ThumbsDown
                                size={30}
                                strokeWidth={1}
                                className="text-red-500 transition-all hover:fill-red-100"

                            />
                            <span className="mt-2">Bad</span>
                        </button>
                    </div>
                    <div className="flex justify-center mt-4">
                        <Button
                            className="secondary-button"
                            onClick={() => {
                                handleSubmit("skipped");
                                setIsCallDialogOpen(false);
                                setIsFeedbackFormOpen(false);
                            }}
                        >
                            Skip
                        </Button>
                    </div>

                    {showDialog && (
                        <div className="flex flex-col gap-4 p-6 border-t mt-4">
                            <div className="flex flex-col gap-2">
                                <label className="">Severity Level</label>
                                <select
                                    value={severity}
                                    onChange={(e) => setSeverity(e.target.value)}
                                    className="border p-2 rounded-md"
                                >
                                    <option value="">Select severity</option>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                </select>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="">How was the call quality and experience?</label>
                                <textarea
                                    value={callQuality}
                                    onChange={(e) => setCallQuality(e.target.value)}
                                    className="border p-2 rounded-md min-h-[100px] resize-none"
                                    placeholder="Rate your call experience..."
                                />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="">Any suggestions for improvement?</label>
                                <textarea
                                    value={suggestions}
                                    onChange={(e) => setSuggestions(e.target.value)}
                                    className="border p-2 rounded-md min-h-[100px] resize-none"
                                    placeholder="Your suggestions for better calls..."
                                />
                            </div>
                            <div className="flex flex-col">
                                <label className="">Enter your name</label>
                                <input
                                    value={userName}
                                    onChange={(e) => setUserName(e.target.value)}
                                    className="border p-2 rounded-md"
                                    placeholder="Enter your name"
                                />
                            </div>
                            <div className="btn-wrapper" style={{ paddingBottom: "0px", paddingTop: "10px" }}>
                                <Button
                                    className="app-nurix-button flex flex-row items-center justify-center gap-1 hover:bg-blue-800"
                                    onClick={() => handleSubmit("negative")}
                                    disabled={showDialog && (!severity || callQuality.trim() === "" || suggestions.trim() === "" || userName.trim() === "")}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    )}
                </main>
            </DialogContent>
        </Dialog>
    );
}
